import { useEffect, useState, useMemo } from 'react';
import axiosInstance from '../api/axiosInstance';
import { DomainKey } from '../config/domains';

const baseUrl = process.env.REACT_APP_API_URL;

interface Precheck {
  id: number;
  reg: string;
  srv: string[];
  genInfo: {
    genComp: string;
    genFullName: string;
    genEmail: string;
  };
  sourceDomain: DomainKey;
  createdAt: string;
}

interface UsePrechecksParams {
  search?: string;
  page?: number;
  limit?: number;
}

const usePrechecks = (params: UsePrechecksParams = {}) => {
  const [allPrechecks, setAllPrechecks] = useState<Precheck[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { search = '', page = 1, limit = 10 } = params;

  // Fetch all prechecks once on mount
  useEffect(() => {
    const fetchPrechecks = async () => {
      try {
        const res = await axiosInstance.get(`${baseUrl}/admin/precheck`);
        const sortedPrechecks = res.data.sort(
          (a: Precheck, b: Precheck) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setAllPrechecks(sortedPrechecks);
      } catch (err) {
        setError('Error fetching prechecks');
        console.error('Error fetching prechecks:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPrechecks();
  }, []);

  // Client-side filtering and pagination
  const { prechecks, totalCount, totalPages } = useMemo(() => {
    // Filter prechecks based on search term (company name)
    let filteredPrechecks = allPrechecks;

    if (search.trim()) {
      filteredPrechecks = allPrechecks.filter(
        (precheck: Precheck) =>
          precheck.genInfo?.genComp?.toLowerCase().includes(search.toLowerCase()) ||
          precheck.genInfo?.genFullName?.toLowerCase().includes(search.toLowerCase()) ||
          precheck.genInfo?.genEmail?.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Calculate pagination
    const totalCount = filteredPrechecks.length;
    const totalPages = Math.ceil(totalCount / limit);

    // Get prechecks for current page
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedPrechecks = filteredPrechecks.slice(startIndex, endIndex);

    return {
      prechecks: paginatedPrechecks,
      totalCount,
      totalPages,
    };
  }, [allPrechecks, search, page, limit]);

  return {
    prechecks,
    totalCount,
    totalPages,
    currentPage: page,
    loading,
    error,
  };
};

export default usePrechecks;
